import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { getIssuancePlanHistoryApi } from "helpers/fakebackend_helper";
import { IssuancePlan, IssuancePlanHistoryEntry } from "../types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

interface HistoryTimelineModalProps {
  isOpen: boolean;
  plan: IssuancePlan | null;
  onClose: () => void;
}

// Cau hinh icon + mau theo tung loai action — dung de ve roadmap (khong phu thuoc DEFAULT_BADGE_COLORS
// vi cac action huy export (CancelPrintExport/CancelQrImageExport) khong gan voi 1 status cu the).
const ACTION_VISUAL: Record<string, { icon: string; color: string }> = {
  Created: { icon: "ri-flag-line", color: "warning" },
  Approved: { icon: "ri-checkbox-circle-line", color: "info" },
  SentPrint: { icon: "ri-printer-line", color: "primary" },
  Accept: { icon: "ri-archive-line", color: "secondary" },
  Cancel: { icon: "ri-close-circle-line", color: "danger" },
  ResumeTicketCreation: { icon: "ri-refresh-line", color: "success" },
  CancelPrintExport: { icon: "ri-file-forbid-line", color: "warning" },
  CancelQrImageExport: { icon: "ri-qr-scan-2-line", color: "warning" },
};

const getActionVisual = (action: string) =>
  ACTION_VISUAL[action] || { icon: "ri-time-line", color: "secondary" };

const HistoryTimelineModal: React.FC<HistoryTimelineModalProps> = ({ isOpen, plan, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<IssuancePlanHistoryEntry[]>([]);

  useEffect(() => {
    if (!isOpen || !plan) return;

    let cancelled = false;
    setLoading(true);
    getIssuancePlanHistoryApi(plan.id)
      .then((response: any) => {
        if (cancelled) return;
        const items = response?.data || response || [];
        setEntries(Array.isArray(items) ? items : []);
      })
      .catch((error: any) => {
        if (cancelled) return;
        toast.error(error?.message || st('khong_the_tai_lich_su_ke_message'));
        setEntries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, plan]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader className="bg-light p-3" toggle={onClose}>
        {st('lich_su_ke_hoach_title')} {plan?.planCode}
      </ModalHeader>
      <ModalBody>
        {loading && (
          <div className="d-flex align-items-center gap-2 text-muted py-3">
            <Spinner size="sm" /> {st('dang_tai_lich_su_text')}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-muted py-3">{st('chua_co_lich_su_thao_tac_text')}</div>
        )}

        {!loading && entries.length > 0 && (
          <div className="d-flex align-items-start overflow-auto py-2" style={{ gap: 0 }}>
            {entries.map((entry, index) => {
              const visual = getActionVisual(entry.action);
              const isLast = index === entries.length - 1;
              return (
                <React.Fragment key={entry.id}>
                  <div className="d-flex flex-column align-items-center text-center px-2" style={{ minWidth: 160 }}>
                    <div
                      className={`d-flex align-items-center justify-content-center rounded-circle bg-${visual.color}-subtle text-${visual.color} mb-2`}
                      style={{ width: 44, height: 44, fontSize: "1.25rem" }}
                    >
                      <i className={visual.icon}></i>
                    </div>
                    <div className="fw-semibold small">{entry.actionName || entry.action}</div>
                    <div className="text-muted small mt-1">{moment(entry.createdAt).format("DD/MM/YYYY HH:mm")}</div>
                    <div className="text-muted small">{entry.actorName || `#${entry.actorId}`}</div>
                    {entry.reason && (
                      <div className="text-danger small mt-1 text-break">"{entry.reason}"</div>
                    )}
                  </div>
                  {!isLast && (
                    <div className="d-flex align-items-center justify-content-center text-muted" style={{ minWidth: 32, marginTop: 20 }}>
                      <i className="ri-arrow-right-line fs-4"></i>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default HistoryTimelineModal;
